// src/AuthorList.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  InputAdornment,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { collection, getDocs, query, orderBy, addDoc, where, limit, serverTimestamp } from "firebase/firestore";
import { db } from "./firebaseConfig";
import toast from "react-hot-toast";

/* --------------------------------------
   Helpers (kept local to avoid deps)
--------------------------------------- */
const slugify = (s) =>
  (s || "")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

function highlight(text, needle) {
  if (!needle) return text;
  const idx = text.toLowerCase().indexOf(needle.toLowerCase());
  if (idx === -1) return text;
  const before = text.slice(0, idx);
  const mid = text.slice(idx, idx + needle.length);
  const after = text.slice(idx + needle.length);
  return (
    <>
      {before}
      <mark style={{ background: "rgba(255, 214, 102, .6)", padding: "0 2px" }}>{mid}</mark>
      {after}
    </>
  );
}

/* --------------------------------------
   Component
--------------------------------------- */
export default function AuthorList() {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    loadAuthors();
  }, []);

  const loadAuthors = async () => {
    try {
      setLoading(true);
      const ref = collection(db, "hymn_authors");
      // keep ordering stable by name
      const qy = query(ref, orderBy("name"));
      const snap = await getDocs(qy);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAuthors(rows);
    } catch (e) {
      console.error("Failed to load authors:", e);
      toast.error("Failed to load authors.");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return authors;
    const q = search.trim().toLowerCase();
    return authors.filter((a) => (a.name || "").toLowerCase().includes(q));
  }, [authors, search]);

  const handleAddAuthor = async () => {
    const name = (newName || "").trim();
    if (!name) {
      toast.error("Enter an author name.");
      return;
    }
    try {
      setAdding(true);
      const name_slug = slugify(name);

      // Prevent duplicates (by slug)
      const existsQ = query(
        collection(db, "hymn_authors"),
        where("name_slug", "==", name_slug),
        limit(1)
      );
      const existsSnap = await getDocs(existsQ);
      if (!existsSnap.empty) {
        toast("Author already exists — selected it.");
        setNewName("");
        return;
      }

      await addDoc(collection(db, "hymn_authors"), {
        name,
        name_slug,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      toast.success("Author added.");
      setNewName("");
      await loadAuthors();
    } catch (e) {
      console.error("Add author failed:", e);
      toast.error("Add failed. Check console for details.");
    } finally {
      setAdding(false);
    }
  };

  /* ---------------- UI ---------------- */
  if (loading) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading authors…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Authors ({authors.length})
        </Typography>

        {/* Full-width Material search */}
        <TextField
          placeholder="Search authors…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {/* Add author row (kept from your original behavior) */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          display: "flex",
          gap: 1,
          alignItems: "center",
          border: "1px solid #eee",
        }}
      >
        <TextField
          label="Add new author"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          fullWidth
        />
        <Button
          onClick={handleAddAuthor}
          variant="contained"
          startIcon={<AddIcon />}
          disabled={adding}
        >
          {adding ? "Adding…" : "Add"}
        </Button>
        <Tooltip title="Refresh list">
          <IconButton onClick={loadAuthors}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* List */}
      {filtered.length === 0 ? (
        <Typography sx={{ opacity: 0.8 }}>No authors match your search.</Typography>
      ) : (
        filtered.map((a) => (
          <Paper
            key={a.id}
            sx={{
              p: 2,
              mb: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              border: "1px solid #eee",
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {highlight(a.name || "Unnamed", search)}
              </Typography>
              {a.name_slug ? (
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {a.name_slug}
                </Typography>
              ) : null}
            </Box>
          </Paper>
        ))
      )}

      <Divider sx={{ mt: 3, mb: 1.5 }} />

      <Typography variant="caption" sx={{ opacity: 0.65 }}>
        Tip: This page is just for managing authors. When adding/editing a hymn, you can search
        for an existing author or click the “+” icon to create one on the fly (no duplicates).
      </Typography>
    </Box>
  );
}
